import { Injectable } from '@angular/core';
import { ApiEntityService } from '@core/api/api-entity.service';
import { Automapper } from '@core/api/mapper';
import { UserNamespacedResourceBaseUrlBuilder } from '@core/user-namespaced-resource-base-url-builder.service';

import { ClientDetails } from '@core/models/client-details.model';
import { ClientDetailsState, ClientDetailsStore } from './client-details.store';
import { getEntityType, getIDType } from '@datorama/akita';
import { HttpUpdateConfig } from '@datorama/akita-ng-entity-service';
import { tap, finalize, share } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ClientDetailsService extends ApiEntityService<ClientDetailsState> {
  private pendingRequests = new Map<string, Observable<ClientDetails>>();

  constructor(store: ClientDetailsStore, urlBuilder: UserNamespacedResourceBaseUrlBuilder) {
    super(store, urlBuilder, Automapper.create(ClientDetails));
    this.overrideResourceName('clients');
  }

  // Override the get method to add caching for individual client details requests
  public get<T>(id?: any, config?: any): Observable<T> {
    // Only apply caching for individual client details requests (when id is provided)
    if (id && typeof id === 'string') {
      return this.getClientDetailsWithCache(id) as Observable<T>;
    }

    // For other requests (collections, etc.), use the original method
    return super.get<T>(id, config);
  }

  private getClientDetailsWithCache(id: string): Observable<ClientDetails> {
    // Check if client details are already in store
    const existingClientDetails = this.store.getValue().entities[id];
    if (existingClientDetails) {
      return of(existingClientDetails);
    }

    // Check if there's already a pending request for this client details
    if (this.pendingRequests.has(id)) {
      return this.pendingRequests.get(id)!;
    }

    // Make new API request and cache it
    const request$ = super.get<ClientDetails>(id).pipe(
      tap((clientDetails) => {
        // Store the client details and set as active
        this.store.replace(id, clientDetails);
        this.store.setActive(id);
      }),
      finalize(() => {
        // Remove from pending requests when complete
        this.pendingRequests.delete(id);
      }),
      share() // Share the observable to prevent multiple HTTP calls
    );

    // Cache the pending request
    this.pendingRequests.set(id, request$);
    return request$;
  }

        update<T>(
    id: getIDType<ClientDetailsState>,
    entity: Partial<getEntityType<ClientDetailsState>> | FormData,
    config?: HttpUpdateConfig<T>,
  ) {
    return super.update(id, entity, config).pipe(tap((result) => this.store.update(result)));
  }
}
